import { forwardRef, useEffect } from 'react';
import { Portal } from 'react-portal';

import type { BoxProps } from '@hivebrite/design-system/Box';
import { Modal } from '@hivebrite/design-system/Dialog';
import type { ModalElement } from '@hivebrite/design-system/Dialog';

import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import type { ModalWithTemplateProps } from '../types';

export default forwardRef<
  ModalElement,
  ModalWithTemplateProps & {
    bodyProps?: BoxProps;
    footProps?: BoxProps;
    headProps?: BoxProps;
    isFixed?: boolean;
    isOpen: boolean;
    isSticky?: boolean;
    style?: React.CSSProperties;
    shouldNotifyMobileApp?: boolean;
  }
>(
  (
    {
      bodyProps,
      center,
      children,
      customHeight,
      customZIndex,
      dataTestId,
      enableEscAction,
      footer,
      footProps,
      header,
      headProps,
      isFixed,
      isLoading,
      isOpen,
      isSticky,
      onCloseModal,
      pb,
      pl,
      pr,
      pt,
      screenReadersCloseButtonText,
      style,
      shouldNotifyMobileApp = true,
      width,
    },
    ref,
  ) => {
    const frameTheme = useFrameTheme();

    const onDismiss = () => {
      onCloseModal?.();

      if (
        shouldNotifyMobileApp &&
        typeof window?.ReactNativeWebView?.postMessage === 'function'
      ) {
        // The rule target window.postMessage, but we are using ReactNativeWebView.postMessage
        // https://github.com/sindresorhus/eslint-plugin-unicorn/blob/v48.0.1/docs/rules/require-post-message-target-origin.md
        window.ReactNativeWebView.postMessage('dialogIsClosed');
      }
    };

    useEffect(() => {
      if (
        isOpen &&
        shouldNotifyMobileApp &&
        typeof window?.ReactNativeWebView?.postMessage === 'function'
      ) {
        // The rule target window.postMessage, but we are using ReactNativeWebView.postMessage
        // https://github.com/sindresorhus/eslint-plugin-unicorn/blob/v48.0.1/docs/rules/require-post-message-target-origin.md
        window.ReactNativeWebView.postMessage('dialogIsOpen');
      }
    }, [isOpen, shouldNotifyMobileApp]);

    return (
      <Portal>
        <ThemeProvider
          fontBrightness={frameTheme.fontBrightness}
          theme={frameTheme.themeName}
        >
          <Modal
            bg={frameTheme.backgroundColor}
            bodyProps={bodyProps}
            canDismissOnEsc={enableEscAction}
            center={center}
            closeButtonText={screenReadersCloseButtonText}
            dataTestId={dataTestId}
            footer={footer}
            footProps={footProps}
            header={header}
            headProps={headProps}
            height={customHeight}
            isFixed={isFixed}
            isLoading={isLoading}
            isOpen={isOpen}
            isSticky={isSticky}
            onDismiss={onDismiss}
            pb={pb}
            pl={pl}
            pr={pr}
            pt={pt}
            // To be fixed when we choose between the two or make a V3.
            // @ts-expect-error V1 and V2 types are incompatible.
            ref={ref}
            style={style}
            width={width}
            zIndex={customZIndex}
          >
            {children}
          </Modal>
        </ThemeProvider>
      </Portal>
    );
  },
);
